
import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Box } from '@mui/material';

import { MAIN_MODAL_COMPACT_ACTIONS_PADDING_TOP } from './mainModalCompact.constants';
import { useMainModalLayout } from './MainModalLayoutContext';
import { OnboardingModalActionsCompactContext } from './OnboardingModalActionsCompactContext';
import { useMainModalSmallScreenLayout } from './useMainModalSmallScreenLayout';

export { useOnboardingModalActionsCompact } from './OnboardingModalActionsCompactContext';

type OnboardingModalActionsProps = Readonly<{
  children: ReactNode;
}>;

/**
 * Renders primary step actions in the modal footer on small-screen viewports; otherwise renders inline.
 * When the compact breakpoint toggles or the footer mount attaches, `children` remount (fragment host
 * vs portal container). Prefer idempotent mount effects or lift durable state outside this subtree.
 * Applies {@link MAIN_MODAL_COMPACT_ACTIONS_PADDING_TOP} in compact mode so steps do not hard-code it.
 */
export function OnboardingModalActions({ children }: OnboardingModalActionsProps) {
  const layout = useMainModalLayout();
  const compact = useMainModalSmallScreenLayout();
  const target = layout?.footerMount ?? null;

  const withCompactPadding = compact ? (
    <Box
      sx={{
        pt: MAIN_MODAL_COMPACT_ACTIONS_PADDING_TOP,
        width: '100%',
        boxSizing: 'border-box',
      }}
    >
      { children }
    </Box>
  ) : (
    children
  );

  return (
    <OnboardingModalActionsCompactContext.Provider value={ compact }>
      { compact && target
        ? createPortal(withCompactPadding, target)
        : withCompactPadding }
    </OnboardingModalActionsCompactContext.Provider>
  );
}
